import { ChangeEvent, useState } from 'react';

import { createProcesso, type Processo } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TRIBUNAIS } from '@/constants/tribunais';
import { useNotification } from '@/context/NotificationProvider';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { maskNumeroProcesso } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { cn } from '@/lib/utils';

const NUMERO_PROCESSO_REGEX = /^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/;

const INITIAL_FORM = { numero: '', tribunal: '', classe: '', status: 'ATIVO', tipo: 'ACOMPANHAMENTO' };

const TRIBUNAL_OPTIONS: ComboboxOption[] = TRIBUNAIS.map((t) => ({
  value: t.sigla,
  label: `${t.sigla} — ${t.nome}`,
  keywords: t.uf ? [t.uf] : undefined,
}));

// --- Dialog ---

interface ProcessoCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProcessoCreateDialog({ open, onOpenChange }: ProcessoCreateDialogProps) {
  const { showSuccess, showError } = useNotification();
  const [form, setForm] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const validateCreateForm = () => {
    const errors: Record<string, string> = {};
    if (!form.numero.trim()) {
      errors.numero = 'Número do processo é obrigatório';
    } else if (!NUMERO_PROCESSO_REGEX.test(form.numero.trim())) {
      errors.numero = 'Informe os 20 dígitos do número do processo';
    }
    if (!form.tribunal.trim()) {
      errors.tribunal = 'Tribunal é obrigatório';
    }
    if (!form.classe.trim()) {
      errors.classe = 'Classe processual é obrigatória';
    }
    return errors;
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setFormErrors({});
  };

  const mutation = useOfflineMutation({
    mutationFn: createProcesso,
    outbox: {
      type: 'processo_criar',
      getPayload: (variables) => ({
        method: 'POST',
        url: '/processos',
        body: variables,
      }),
      getDescription: (variables) => `Criar processo ${(variables as Partial<Processo>).numero || ''}`.trim(),
    },
    invalidateKeys: [[...queryKeys.processos.all]],
    onSuccess: () => {
      showSuccess('Processo criado com sucesso');
      onOpenChange(false);
      resetForm();
    },
    onQueued: () => {
      showSuccess('Processo criado com sucesso (será sincronizado quando houver conexão)');
      onOpenChange(false);
      resetForm();
    },
    onError: (err) => {
      // Número já cadastrado (409) é erro de negócio do campo Número — mostra
      // inline junto ao campo em vez de toast. Demais erros (500, rede) vão para
      // o helper central, que também reporta ao Sentry.
      if (classifyError(err).kind === 'conflict') {
        setFormErrors((prev) => ({ ...prev, numero: classifyError(err).message }));
        return;
      }
      notifyMutationError(err, showError);
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = event.target.name as string;
    const value = name === 'numero' ? maskNumeroProcesso(event.target.value) : event.target.value;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleTribunalChange = (sigla: string) => {
    setForm((prev) => ({ ...prev, tribunal: sigla }));
    if (formErrors.tribunal) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next.tribunal;
        return next;
      });
    }
  };

  const handleSubmit = () => {
    const errors = validateCreateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    // `mutate` (não `mutateAsync`): a rejeição é tratada por onError, sem virar
    // unhandled rejection — era a causa do "nada acontece" (o erro sumia e o
    // Sentry só pegava via onunhandledrejection).
    mutation.mutate({
      ...form,
      possui_permissao_peticionar: form.tipo === 'PATROCINIO'
    } as Partial<Processo>);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) resetForm(); onOpenChange(val); }}>
      <DialogContent className="w-full sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Adicionar Processo</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Label>Número do Processo *</Label>
            <Input
              name="numero"
              value={form.numero}
              onChange={handleChange}
              placeholder="0000000-00.0000.0.00.0000"
              className={cn(formErrors.numero && 'border-destructive')}
            />
            {formErrors.numero && (
              <span className="text-xs text-destructive">{formErrors.numero}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label>Tribunal *</Label>
            <Combobox
              options={TRIBUNAL_OPTIONS}
              value={form.tribunal}
              onValueChange={handleTribunalChange}
              placeholder="Selecione o tribunal..."
              searchPlaceholder="Buscar tribunal..."
              emptyMessage="Nenhum tribunal encontrado."
              hasError={!!formErrors.tribunal}
            />
            {formErrors.tribunal && (
              <span className="text-xs text-destructive">{formErrors.tribunal}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label>Classe Processual *</Label>
            <Input
              name="classe"
              value={form.classe}
              onChange={handleChange}
              placeholder="Ex: Ação Trabalhista"
              className={cn(formErrors.classe && 'border-destructive')}
            />
            {formErrors.classe && (
              <span className="text-xs text-destructive">{formErrors.classe}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label>Status</Label>
            <Select
              value={form.status}
              onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ATIVO">Ativo</SelectItem>
                <SelectItem value="ENCERRADO">Encerrado</SelectItem>
                <SelectItem value="SUSPENSO">Suspenso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label>Tipo</Label>
            <Select
              value={form.tipo}
              onValueChange={(value) => setForm((prev) => ({ ...prev, tipo: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACOMPANHAMENTO">Acompanhamento</SelectItem>
                <SelectItem value="PATROCINIO">Patrocínio</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending || !form.numero.trim() || !form.tribunal.trim() || !form.classe.trim()}
          >
            {mutation.isPending ? 'Criando...' : 'Criar Processo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
